import React, { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useTranslation } from 'react-i18next';
import { QualifyReport } from './useQualifyRequests';

/** Hauteur au-delà de laquelle la liste des refus défile, pour garder le bouton visible. */
const ERRORS_MAX_HEIGHT = 160;

type QualifyReportMessageProps = {
  report: QualifyReport;
};

type SummaryLineProps = {
  icon: ReactNode;
  text: string;
};

function SummaryLine({ icon, text }: SummaryLineProps) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {icon}
      <Typography variant="body2">{text}</Typography>
    </Stack>
  );
}

/**
 * Bilan d'une qualification qui demande une action : le décompte d'abord, puis un encadré
 * par demande refusée. La modale qui l'affiche centre et grise son texte, pensé pour une
 * phrase courte : ce bilan reprend la main sur l'alignement et la couleur.
 */
export default function QualifyReportMessage({ report }: QualifyReportMessageProps) {
  const { t } = useTranslation();

  return (
    <Stack spacing={2} sx={{ color: 'text.primary', textAlign: 'left', whiteSpace: 'normal' }}>
      <Typography variant="subtitle1" fontWeight={600} textAlign="center">
        {t('interventionCreation.qualification.reportTitle')}
      </Typography>

      <Stack spacing={0.5}>
        <SummaryLine
          icon={<CheckCircleOutlineIcon color="success" fontSize="small" />}
          text={t('interventionCreation.qualification.success', { count: report.qualifiedCount })}
        />
        {report.incompleteCount > 0 && (
          <SummaryLine
            icon={<ErrorOutlineIcon color="warning" fontSize="small" />}
            text={t('interventionCreation.qualification.incomplete', {
              count: report.incompleteCount
            })}
          />
        )}
        {report.failedCount > 0 && (
          <SummaryLine
            icon={<HighlightOffIcon color="error" fontSize="small" />}
            text={t('interventionCreation.qualification.partialFailure', {
              count: report.failedCount
            })}
          />
        )}
      </Stack>

      {report.errors.length > 0 && (
        <Stack
          component="ul"
          spacing={1}
          sx={{ m: 0, p: 0, listStyle: 'none', maxHeight: ERRORS_MAX_HEIGHT, overflowY: 'auto' }}
        >
          {report.errors.map((error) => (
            <Box
              component="li"
              key={error.requestId}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderLeft: 4,
                borderLeftColor: 'error.main',
                borderRadius: 1,
                px: 1.5,
                py: 1
              }}
            >
              <Typography variant="body2">{error.message}</Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
